// Debug logger for capturing structured logs in the browser
// Logs are stored in localStorage and can be exported

interface DebugLog {
  location: string;
  message: string;
  data: Record<string, unknown>;
  timestamp: number;
  sessionId: string;
  hypothesisId?: string;
}

const DEBUG_SESSION_KEY = 'kairos_debug_session';
const MAX_LOGS = 500; // Prevent localStorage overflow

export function debugLog(log: Omit<DebugLog, 'timestamp' | 'sessionId'>) {
  try {
    const fullLog: DebugLog = {
      ...log,
      timestamp: Date.now(),
      sessionId: 'debug-session',
    };

    // Get existing logs
    const existing = localStorage.getItem(DEBUG_SESSION_KEY);
    const logs: DebugLog[] = existing ? JSON.parse(existing) : [];

    // Add new log
    logs.push(fullLog);

    // Keep only the last MAX_LOGS entries
    if (logs.length > MAX_LOGS) {
      logs.splice(0, logs.length - MAX_LOGS);
    }

    // Save back to localStorage
    localStorage.setItem(DEBUG_SESSION_KEY, JSON.stringify(logs));

    // Also log to console for immediate visibility
    console.log(`[DEBUG] ${log.location}:`, log.message, log.data);
  } catch (error) {
    console.error('[DEBUG] Failed to write log:', error);
  }
}

export function clearDebugLogs() {
  try {
    localStorage.removeItem(DEBUG_SESSION_KEY);
    console.log('[DEBUG] Logs cleared');
  } catch (error) {
    console.error('[DEBUG] Failed to clear logs:', error);
  }
}

export function exportDebugLogs(): string {
  try {
    const existing = localStorage.getItem(DEBUG_SESSION_KEY);
    const logs: DebugLog[] = existing ? JSON.parse(existing) : [];
    return logs.map(log => JSON.stringify(log)).join('\n');
  } catch (error) {
    console.error('[DEBUG] Failed to export logs:', error);
    return '';
  }
}

// Expose to window for easy console access
if (typeof window !== 'undefined') {
  (window as any).debugExport = exportDebugLogs;
  (window as any).debugClear = clearDebugLogs;
}
